import { isEmpty, some } from 'lodash-es';
import * as api from './../../api/api.service';
import * as helpers from './../../api/helpers/permissions.service';
import * as rfps from './../../api/rfps/rfp.service';
import * as featuresApi from './../../api/features.service';

import angular = require('angular');
import { Subscription } from 'rxjs';
export enum RfpTypes {
  REQUEST_FOR_PRICING = 'RFP',
  MEMBER_RECRUITING = 'MR',
  SELLER_INITIATED_OFFER = 'SIO',
}

interface JQuery {
  noty: any;
}

/**
 * This powers the sub-nav for all the response pages (Overview, Cross-Reference, Structure, Pricing, Impact)
 *
 * This is also used on the old Pricing Model page (see responseNavJspxWrapper)
 */
class ResponseNav {
  static bindings = {
    account: '<',
    activePage: '@',
    rfp: '<',
    offers: '<?',
    offerId: '<',
    offerUpdated: '&',
    onSubmitResponse: '&?',
  };

  account;
  activePage: string;
  rfp: rfps.RfpVersionTwo; // We use the new version here, make sure the new version is passed in

  /**
   * Certain things (is Impact link enabled, are offers complete) generally rely on the ResponsePermissions service
   *
   * The Offer Structure and Offer Pricing pages check the actual offers since those pages can change which offers are complete (after permissions were loaded)
   *
   * The submit response modal will load the offers if they were not passed in
   */
  offers;
  _oldOffers;
  onSubmitResponse;
  featureList: featuresApi.FeatureFlags;
  RfpTypes = RfpTypes;

  constructor(
    private $window,
    // private $routeParams,
    private api: api.IApiService,
    private NavMessages,
    private OfferHelper,
    private ResponsePermissions: helpers.ResponsePermissions,
    private ResponseNavSubmitResponseModal,
    private LoadingIndicator,
    private $bsModal,
    private ResponseNavSubmitOffersModal,
  ) {
    'ngInject';
  }

  $onInit() {
    this._oldOffers = angular.copy(this.offers);

    // Some pages, such as offer structure and pricing have already loaded the permissions while others have not
    if (!this.ResponsePermissions.arePermissionsLoaded()) {
      this.loadPermissions();
    }
    this.loadFeatureFlag();
  }

  $doCheck() {
    if (!angular.equals(this._oldOffers, this.offers)) {
      this._oldOffers = angular.copy(this.offers);
      this.disableParticipant();
    }
  }

  loadPermissions() {
    if (this.account && this.rfp) {
      let permissionsLoader = this.api.users.getResponsePermissions(
        this.account.userId,
        this.rfp.id,
        this.account.organization.id,
      );

      this.LoadingIndicator.waitFor(permissionsLoader);

      permissionsLoader.then((permissions) => {
        this.ResponsePermissions.setPermissions(permissions);
      });

      return permissionsLoader;
    }
  }

  loadFeatureFlag() {
    let featureLoader = this.api.features
      .getFeatures()
      .then((features) => (this.featureList = features));
    this.LoadingIndicator.waitFor(featureLoader);

    return featureLoader;
  }

  /**
   * We have to handle certain permissions differently on these two pages
   */
  isOfferStructureOrPricingPage() {
    return (
      this.activePage === 'offerStructure' || this.activePage === 'offerPricing'
    );
  }

  /**
   * We always show the Send Response button unless the Retract Response button is visible
   * The Send Response button will be visible, even after the offer has been submitted and contracted
   *
   * Even though the button is visible, it will be disabled if the user cannot send the response (or has already sent it)
   */
  isSendResponseVisible() {
    return !this.isRetractResponseVisible();
  }

  /**
   * Disable the button if it's already been submited or if it's expired
   * Also disable if there are no offers
   */
  isSendResponseEnabled(featureList) {
    if (!this.offers && !this.rfp) {
      return false;
    }

    // check this.offers on structure & pricing pages, otherwise this.rfp.offers
    let offers = this.offers || this.rfp.offers;
    return (
      this.rfp &&
      !this.isExpired() &&
      !isEmpty(offers) &&
      !(this.isSubmitted() && !this.ResponsePermissions.canSubmitOffer()) &&
      !this.isSellerDisqualifiedFromTheRfp()
    );
  }

  /**
   * Check whether we can submit the offer, if so, open the Send Response modal, otherwise show error msg
   *
   * Note, we reload the permissions each time the user clicks the submitting offer button to be sure they're current
   * in case the user (or anyone else) has taken any actions since they were loaded
   */
  sendResponseIfValid() {
    // Reload the permissions and show any warnings
    this.loadPermissions().then((permissions) => {
      if (this.isExpired()) {
        this.NavMessages.showErrorMessage(
          '<b>This offer has expired.</b> Please contact your <b>apt</b>itude sales consultant for more information.',
        );

        return false;
      } else if (!this.ResponsePermissions.canSubmitOffer()) {
        this.showValidationMessage(
          this.ResponsePermissions.getDenialReason('SUBMIT_OFFERS'),
        );

        return false;
      } else {
        this.submitOffer();
      }
    });
  }

  /**
   * Send the response
   *
   * NOTE: Don't call this directly. Call sendResponseIfValid, and that will call this if we're good to submit
   *
   * Once the offer is submitted, we need to disable the submit button and remove any offers that were not submitted.
   *
   * Our options are:
   *   1) change the flags manually here,
   *   2) reload the data to get the new values here, or
   *   3) delegate to the parent component to reload the data as necessary.
   *
   * I'm choosing 3. This is not perfect because if the parent component does not reload the data, then this button won't reflect correctly
   * But it's better for the parent to reload the data and pass it in with one way binding than to reload it here. To partially address this,
   * We call our own reload method to reload the data if no onSubmitResponse handler was passed in - i.e. for pricing models page
   *
   * Data to reload in parent components:
   * - rfp
   * - offers
   */
  /**@deprecated**/
  sendResponse() {
    let submitResponseModal = this.ResponseNavSubmitResponseModal.openModal(
      this.account,
      this.rfp,
      this.offers,
    );

    submitResponseModal.then((offer) => {
      // Reload the permissions to reflect the submitted state - always reload permissions here instead of parent component
      this.loadPermissions();

      // Notify parent component to reload rfp, offers, etc. as necesssary
      if (this.onSubmitResponse) {
        this.onSubmitResponse();
      } else {
        this.reloadRfp();
      }

      this.NavMessages.showSuccessMessage('Your offer was successfully sent');
    });
  }

  submitOffer() {
    let submitResponseModal = this.ResponseNavSubmitOffersModal.openModal(
      this.account,
      this.rfp,
      this.offers,
      this.featureList,
    );

    submitResponseModal.then((offer) => {
      // Reload the permissions to reflect the submitted state - always reload permissions here instead of parent component
      this.loadPermissions();

      // Notify parent component to reload rfp, offers, etc. as necesssary
      if (this.onSubmitResponse) {
        this.onSubmitResponse();
      } else {
        this.reloadRfp();
      }

      this.NavMessages.showSuccessMessage('Your offer was successfully sent');
    });
  }

  /**
   * Only call this if no onSubmitResponse handler was passed to this component
   * If an outside page passes in offers, it can be responsible for reloading their offers
   * If we rely on the rfp to get the offers, then we'll get that update when the rfps are reloaded
   */
  reloadRfp() {
    if (!this.onSubmitResponse) {
      let rfpLoader = this.api.rfps
        .getRfp(this.rfp.id)
        .then((rfp) => (this.rfp = rfp));

      this.LoadingIndicator.waitFor(rfpLoader);
    }
  }

  /**
   * Show an error message if we had any of these errors
   */
  showValidationMessage(denialReason: helpers.ResponseDenialReasons) {
    switch (denialReason) {
      case 'OfferAlreadySubmitted':
        this.NavMessages.showErrorMessage(
          `<b>An offer has already been sent to this buyer.</b>
          Please contact your <b>apt</b>itude sales consultant for more information.`,
        );
        break;

      case 'NoSubmitOfferRole':
        this.NavMessages.showErrorMessage(
          `You are not authorized to submit an offer.
          Please contact your <b>apt</b>itude sales representative for more information.`,
        );
        break;

      case 'NoActiveCategoryAddendum':
        this.NavMessages.showErrorMessage(
          `<b>In order to submit this offer you must have an active category addendum.</b>
          Please contact your <b>apt</b>itude sales representative for more information.`,
        );
        break;

      case 'NoValidCpaTemplate':
        this.NavMessages.showErrorMessage(
          `<b>In order to send this offer you must have a valid Committed Pricing Agreement.</b>
          Please contact your <b>apt</b>itude sales representative for more information.`,
        );
        break;

      case 'NoSellerUnspcs':
        this.NavMessages.showErrorMessage(
          `In order to submit this offer you must offer products from an UNSPSC in which the buyer purchases.
          Please contact your <b>apt</b>itude sales representative for more information.`,
        );
        break;

      case 'PricingNotComplete':
        this.NavMessages.showErrorMessage(
          `<b>Pricing information is not complete.</b>
          <a href="/aptitude/static/sellerResponse.html#/offerPricing/${this.rfp.id}">Click here to enter missing prices</a>`,
        );
        break;

      case 'RestrictedFromCategory':
        this.NavMessages.showErrorMessage(
          `Your account is not authorized to submit an offer.
          Please contact your <b>apt</b>itude sales consultant for more information`,
        );
        break;

      case 'NoCrossReferences':
        this.NavMessages.showErrorMessage(
          `In order to submit this offer, you must cross-reference at least one product.
          Please click the cross-reference button and cross-reference your products.`,
        );
        break;
      case 'SellerExcludedByBuyer':
        this.NavMessages.showErrorMessage(
          'Disqualified from this RFP, contact aptitude Support.',
        );
        break;
    }
  }

  /**
   * We only show the Retract Response button if it's possible to retract the response. This will never be disabled while visible.
   *
   * We reload permissions when sending / retracting a response, so it should always be safe to simply rely on the permission
   *
   * On the backend, this permission should be true if it was submitted less than 48 hours ago and is not an SIO
   *
   * This button should show immediately upon submitting a response
   */
  isRetractResponseVisible() {
    return this.ResponsePermissions.canRetractResponse();
  }

  getRetractTooltipMessage() {
    if (!this.isRetractResponseVisible()) {
      return 'Retract function is not available at this time.';
    }
    return 'Retracting all submitted offers is available for 24hours from time of submission.';
  }

  /**
   * Kemi is still building the api for this
   *
   * TODO: This is probably all needs to change when Kemi is done. We should no longer be passing an offer
   */
  retractResponse() {
    let retractResponseLoader = this.api.rfps.sellers.retractResponse(
      this.rfp.id,
      this.account.organization.id,
    );

    this.LoadingIndicator.waitFor(retractResponseLoader);

    retractResponseLoader.then(
      (response) => {
        // TODO: Do we still need this?
        // this.rfp.offers = response; // type for response is wrong -- make sure this returns an array
        // this.offerUpdated(response);

        // Reload permissions to reflect the retracted state
        this.loadPermissions();
        // Notify parent component to reload rfp, offers, etc. as necesssary
        if (this.onSubmitResponse) {
          this.onSubmitResponse();
        } else {
          this.reloadRfp();
        }

        this.NavMessages.showSuccessMessage(
          'Your offer was successfully retracted',
        );
      },
      () => {
        this.NavMessages.showErrorMessage(
          '<b>The system encountered an unexpected error.</b> Please refresh the page and try again.',
        );
      },
    );
  }

  /**
   * Hide the Impact link entirely for solo RFPs
   */
  isOfferImpactLinkVisible() {
    if (
      (this.isGroupOrIndividualCommitment() && this.rfp?.type !== 'SIO') ||
      this.rfp?.type == 'SIO'
    ) {
      return false;
    } else if (this.rfp && this.rfp.groupRfp && this.rfp.type === 'SIO') {
      return true;
    } else {
      return false;
    }
  }

  isPublishedGPORfp() {
    return !(
      this.rfp?.buyer?.organizationType === 'GPO' &&
      this.rfp.status.toLowerCase() === 'published'
    );
  }

  isParticipantsImpactVisible() {
    return (
      this.isOfferImpactLinkVisible() === false ||
      (this.isOfferImpactLinkVisible() === false &&
        this.isGroupOrIndividualCommitment() &&
        this.rfp?.type !== 'SIO') ||
      (this.isOfferImpactLinkVisible() === false && this.rfp.type == 'SIO')
    );
  }

  isGroupOrIndividualCommitment() {
    return this.offers?.some(
      (offer: any) =>
        offer.supportedTerms?.commitmentTypes.indexOf('GROUP') !== -1 ||
        offer.supportedTerms?.commitmentTypes.indexOf(
          'INDIVIDUAL_COMMITMENT',
        ) !== -1,
    );
  }

  disableParticipant(): boolean {
    let disableParticipantsImpactButton = true;
    if (!this.offers && this.rfp?.offers?.length > 0) {
      // If none of the offers are complete, we disable the 'Participants Impact'.
      disableParticipantsImpactButton = !this.rfp?.offers.some((o) => {
        return o.productPricingStatus === 'COMPLETE';
      });
    }

    if (this.offers?.length > 0) {
      // If none of the offers are complete, we disable the 'Participants Impact'.
      disableParticipantsImpactButton = !this.offers.some((o) => {
        return o.productPricingStatus === 'COMPLETE';
      });
    }

    return disableParticipantsImpactButton;
  }

  isOfferImpactEnabled(): boolean {
    if (this.activePage == 'offerImpact') {
      return true;
    }

    if (!this.isOfferStructureOrPricingPage()) {
      return this.ResponsePermissions.canViewOfferImpact();
    }

    if (this.offers && this.offers.length) {
      let areAnyOffersComplete = some(this.offers, (offer) => {
        return this.OfferHelper.isPricingComplete(offer);
      });

      return areAnyOffersComplete;
    }

    return false;
  }

  backToOffer() {
    // Using this.$window.history.go(-1) is not the same thing as going back to the offer. The user may have looked at
    // or edited multiple pricing models while the page was showing and each one appears separately within the history.
    // Going through the history just walks us back through those.
    this.$window.location = `/aptitude/static/sellerResponse.html#/structure/${this.rfp.id}`;
  }

  // FIXME: This logic belongs in a service, not the controller
  isSubmitted() {
    if (!this.rfp) {
      return false;
    }

    let status = this.rfp.responseStatus;

    return (
      status === 'OfferSubmitted' ||
      status === 'OfferAccepted' ||
      status === 'OfferRejected'
    );
  }

  isExpired() {
    if (!this.rfp) {
      return false;
    }

    return this.rfp.responseStatus === 'OfferExpired';
  }

  isSellerDisqualifiedFromTheRfp() {
    return (
      !this.ResponsePermissions.canSubmitOffer() &&
      this.ResponsePermissions.getDenialReason('SUBMIT_OFFERS') ===
        'SellerExcludedByBuyer'
    );
  }
}

export const ResponseNavComponent = {
  bindings: ResponseNav.bindings,
  controller: ResponseNav,
  template: `<aptitude-nav ng-if="$ctrl.activePage !== 'pricingModel'">
             <gutter/>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-overview" url="'/aptitude/static/sellerResponse.html#/rfp/' + $ctrl.rfp.id"
                       active="$ctrl.activePage === 'overview'">
               Overview
             </nav-item>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-participants" url="'/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/participants'">
               Participants
             </nav-item>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-categories" url="'/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/categories'">
               Categories
             </nav-item>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-cross-references"
                        url="'/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/cross-references'"
                       active="$ctrl.activePage === 'crossReferences'">
               Cross-References
             </nav-item>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-offer-structure" url="$ctrl.featureList.PLATFORM_OFFER_UPGRADE_RFP_945971 ? '/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/offer-structure' : '/aptitude/static/sellerResponse.html#/structure/' +$ctrl.rfp.id"
                       active="$ctrl.activePage === 'offerStructure'">
               Offer Structure
             </nav-item>
             <nav-item ng-if="$ctrl.isPublishedGPORfp()" data-test-selector="response-offer-pricing" url="$ctrl.featureList.PLATFORM_OFFER_UPGRADE_RFP_945971 ? '/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/offer-pricing' : '/aptitude/static/sellerResponse.html#/offerPricing/' +$ctrl.rfp.id"
                       active="$ctrl.activePage === 'offerPricing'">
               Offer Pricing
             </nav-item>
             <nav-item url="'/aptitude/static/sellerResponse.html#/impact/' + $ctrl.rfp.id"
                       active="$ctrl.activePage === 'offerImpact'"
                       ng-if="$ctrl.isOfferImpactLinkVisible() && $ctrl.isPublishedGPORfp()"
                       disabled="!$ctrl.isOfferImpactEnabled()">
               Offer Impact
             </nav-item>
             <!-- Added link for seller participants impact -->
             <nav-item data-test-selector="response-participant-impact" url="'/aptitude/static/platform/purchase-planning/'+$ctrl.rfp.id+'/seller-response/participants-impact'"
                       ng-if="$ctrl.isParticipantsImpactVisible() && $ctrl.isPublishedGPORfp()"
                      disabled="$ctrl.disableParticipant()"
                   >
                Participants Impact
             </nav-item>
             <nav-item data-test-selector="seller-bulletin-board" url="'/aptitude/static/platform/rfp/'+$ctrl.rfp.id+'/seller-response/bulletin-board'"
                       >Bulletin Board</nav-item>

             <gap/>
             <nav-download data-test-selector="cross-reference-download" url="'/aptitude/download/crossreference?rfpId=' + $ctrl.rfp.id"
                       ng-if="$ctrl.activePage === 'crossReferences'"></nav-download>
             <nav-download data-test-selector="offer-pricing-download" url="'/aptitude/download/offers/' + $ctrl.offerId + '/products'"
                       ng-if="$ctrl.activePage === 'offerPricing' && $ctrl.offerId"></nav-download>
             <div bs-tooltip="$ctrl.getRetractTooltipMessage()" data-placement="bottom">
              <nav-button class="retract-pricing-model-button" ng-if="$ctrl.isOfferStructureOrPricingPage()" data-test-selector="retract-pricing-model-button" click="$ctrl.retractResponse()" disabled="!$ctrl.isRetractResponseVisible()">
                Retract this Pricing Model
              </nav-button>
             </div>
             <nav-button class="send-response-button" data-test-selector="send-response-button" click="$ctrl.sendResponseIfValid()"
                         disabled="!$ctrl.isSendResponseEnabled($ctrl.featureList)">
               Send Response
             </nav-button>
             <gutter/>
           </aptitude-nav>
           <aptitude-nav ng-if="$ctrl.activePage === 'pricingModel'">
             <gutter/>
             <gap/>
             <nav-button click="$ctrl.backToOffer()">Back to Offer</nav-button>
             <gutter/>
           </aptitude-nav>`,
};

function filterObject(author: any, arg1: ([k, v]: [any, any]) => boolean) {
  throw new Error('Function not implemented.');
}
